import { Component, effect, inject, ViewChild } from '@angular/core';
import { GlobalsStore } from '../../../stores/globals.state';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { getState } from '@ngrx/signals';
import { IndicatorData } from '../../../models/indicator-data';
import { Indicator } from '../../../models/indicator';
import { DataLayer } from '../../../models/data-layer';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { NgIf } from '@angular/common';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { TypeDropdownComponent } from '../../atoms/type-dropdown/type-dropdown.component';
import { DataLayerType } from '../../../models/data-layer-type';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { DataTableComponent } from '../../molecules/data-table/data-table.component';
import { KeyValueDistricts } from '../../../models/geo-data';
import { HeaderComponent } from '../../atoms/header/header.component';
import { MenuModule } from 'primeng/menu';
import { MenuItem, MessageService } from 'primeng/api';
import { MaskComponent } from '../mask/mask.component';
import { SidebarModule } from 'primeng/sidebar';
import { BaseListComponent } from '../base-list/base-list.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { RoutingService } from '../../../services/routing.service';
import { ConfirmPublishDataComponent } from '../../molecules/confirm-publish/confirm-publish-data.component';
import { ConfirmLockDataComponent } from '../../molecules/confirm-lock-data/confirm-lock-data.component';
import { YearListComponent } from '../../molecules/year-list/year-list.component';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { PermissionService } from '../../../services/permission.service';

@Component({
  selector: 'app-indicator-data-detail',
  standalone: true,
  imports: [
    AlertComponent,
    DividerModule,
    ButtonModule,
    NgIf,
    TypeDropdownComponent,
    DataTableComponent,
    HeaderComponent,
    MenuModule,
    MaskComponent,
    SidebarModule,
    ConfirmPublishDataComponent,
    ConfirmLockDataComponent,
    YearListComponent,
    ScrollPanelModule,
  ],
  templateUrl: './indicator-data-detail.component.html',
  styleUrl: './indicator-data-detail.component.scss',
})
export class IndicatorDataDetailComponent extends BaseListComponent<IndicatorData> {
  override sidebarId = 'indicator-data-administration';
  readonly store = inject(IndicatorDataStore);
  readonly activeUserStore = inject(ActiveUserStore);
  readonly globalStore = inject(GlobalsStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly dataLayerStore = inject(DataLayerStore);

  protected activeIndicatorData?: IndicatorData;
  protected selectedIndicator: Indicator | null = null;
  protected selectedIndicatorChild: Indicator | null = null;
  protected selectedLayerType: DataLayerType | null = null;
  protected selectedLayer: DataLayer | null = null;
  protected districts: KeyValueDistricts | null = null;
  protected header = '';
  protected dataFormRouterLink: any = [];
  override menuItems: MenuItem[] | undefined;
  public canModify = true;

  constructor(
    router: Router,
    route: ActivatedRoute,
    messageService: MessageService,
    confirmationDialogService: ConfirmationDialogService,
    routingService: RoutingService,
    private permissionService: PermissionService
  ) {
    super(
      router,
      route,
      messageService,
      confirmationDialogService,
      routingService
    );
    effect(() => {
      const globalState = getState(this.globalStore);
      this.selectedIndicator = globalState.selectedIndicator;
      this.selectedIndicatorChild = globalState.selectedChildIndicator;
      this.selectedLayer = globalState.selectedLayer;
      this.selectedLayerType = globalState.selectedType;
      this.activeIndicatorData = this.globalStore.getActiveIndicatorData();
      this.districts = this.globalStore.getDistrictsFromSelection();

      this.header = `${this.selectedLayerType?.name} ${this.selectedLayer?.year}: ${this.selectedIndicator?.name}`;

      // select indicator for data-detail-edit-form after reload
      if (this.store.selectedId() && !globalState.selectedIndicator) {
        queueMicrotask(() => {
          this.globalStore.setSelectedIndicator(
            this.indicatorStore.getById(this.store.selectedItem().indicator)
          );
        });
      }
      queueMicrotask(() => this.buildMenu());
    });
  }

  /**
   * Builds and configures the menu items based on the state of the activeIndicatorData.
   *
   * The method dynamically creates a menu structure with options like 'edit', 'create new', 'lock', 'publish',
   * and 'delete' depending on the existence and published state of the activeIndicatorData.
   * Each menu item includes labels, icons, and commands that trigger corresponding actions.
   *
   * @return {void} This method does not return any value.
   */
  buildMenu(): void {
    if (!this.selectedIndicator) return;
    const indicatorDataId = this.activeIndicatorData?._id;
    const isPublished = !!this.activeIndicatorData?.published;

    this.setRouterLink(indicatorDataId);
    this.menuItems = [
      {
        visible: this.permissionService.checkIndicatorRights(
          this.selectedIndicator.category,
          indicatorDataId ? 'edit' : 'add'
        ),
        label: indicatorDataId ? 'bearbeiten' : 'neu anlegen',
        icon: indicatorDataId ? 'fas fa-pen' : 'fas fa-circle-plus',
        routerLink: this.dataFormRouterLink,
      },
      {
        visible:
          !!this.activeIndicatorData?._id &&
          isPublished &&
          this.permissionService.checkIndicatorRights(
            this.selectedIndicator.category,
            'lock'
          ),
        label: 'intern schalten',
        icon: 'fas fa-house-user',
        command: (event: any) => {
          this.confirmationDialogService.confirmLockData(
            () => this.lockData(),
            `${this.header} intern schalten?`
          );
        },
      },
      {
        visible:
          !!this.activeIndicatorData?._id &&
          !isPublished &&
          this.permissionService.checkIndicatorRights(
            this.selectedIndicator.category,
            'publish'
          ),
        label: 'veröffentlichen',
        icon: 'fas fa-earth-europe',
        command: (event: any) => {
          this.confirmationDialogService.confirmPublishData(
            () => this.publishData(),
            `${this.header} veröffentlichen?`
          );
        },
      },
      {
        visible:
          !!this.activeIndicatorData?._id &&
          this.permissionService.checkIndicatorRights(
            this.selectedIndicator.category,
            'remove'
          ),
        label: 'löschen',
        icon: 'fas fa-trash',
        command: (event: any) => {
          if (this.activeIndicatorData) {
            this.confirm(
              event,
              this.activeIndicatorData,
              this.header,
              this.selectedIndicator
                ? `Sollen die Daten zum Indikator <i>„${this.indicatorStore.getFullName(this.selectedIndicatorChild || this.selectedIndicator, false)}“</i> für <i>„${this.selectedLayerType?.name}“</i> und das Jahr <i>${this.selectedLayer?.year}</i>, wirklich gelöscht werden?`
                : undefined
            );
          }
        },
      },
    ];

    this.canModify = this.menuItems.some(item => item.visible);
  }

  /**
   * Publishes the active indicator data and updates the relevant data layers if applicable.
   * Ensures that the selected layer and active indicator data are marked as published in their respective stores.
   *
   * @return {void} Does not return a value.
   */
  publishData(): void {
    if (!this.activeIndicatorData) return;

    if (
      this.selectedLayer &&
      this.confirmationDialogService.getPublishDataLayerId()
    ) {
      this.selectedLayer.published = true;
      this.dataLayerStore.update(this.selectedLayer);
    }
    this.activeIndicatorData.published = true;
    this.store.update(this.activeIndicatorData);
  }

  /**
   * Locks the current active indicator data by setting its published status to false
   * and updates the store with the modified data.
   *
   * @return {void} Returns nothing.
   */
  lockData(): void {
    if (!this.activeIndicatorData) return;
    this.activeIndicatorData.published = false;
    this.store.update(this.activeIndicatorData);
  }

  /**
   * Configures the router link for navigating to the data backend with the specified ID.
   *
   * @param {string} [id] - The optional ID to be included in the router link. If not provided, defaults to 'neu'.
   * @return {void} No return value.
   */
  setRouterLink(id?: string): void {
    this.dataFormRouterLink = [
      '/data-backend',
      {
        outlets: {
          bearbeiten: ['data', id ?? 'neu'],
        },
      },
    ];
  }
}
